// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The DMD on a 2-D canvas (M3-32, 08 §5.1).
//
// The phone's portrait layout reduces the backbox to "the DMD strip — 128 × 32 upscaled, amber,
// crisp". The 3-D scene already draws the same raster through a shader with a real dot mask
// (`src/world/dmdPanel.ts`); this is the flat version, for a strip that is not part of the scene
// and must not cost a second WebGL context to draw.
//
// **Crisp means nearest-neighbour, and that means one `drawImage`.** The obvious implementation —
// a filled rectangle per lit dot — is 4,096 draw calls a frame, a quarter of a million a second,
// and it is the sort of thing that measures fine on a desktop and halves the frame rate on the
// device the layout exists for. Instead the raster is written once into a 128 × 32 `ImageData`
// and blown up with smoothing off: one upload and one blit, whatever the strip's size.
//
// **The dot grid is deliberately not drawn.** On a phone the strip is about a tenth of the
// screen — roughly 78 px for 32 rows, so two and a half pixels per dot. A dot mask at that size
// is a grey haze that costs contrast and shows nothing, and the shader version exists for the
// backglass, where the panel is big enough for the structure to read.

import { DMD_LEVELS, dmdPalette, type DmdRaster } from './dmd'

/** What this needs from a 2-D context, so a test can supply it without a canvas. */
export interface Canvas2DLike {
  canvas: { width: number; height: number }
  imageSmoothingEnabled: boolean
  fillStyle: string | CanvasGradient | CanvasPattern
  createImageData: (width: number, height: number) => ImageData
  putImageData: (data: ImageData, x: number, y: number) => void
  drawImage: (
    source: CanvasImageSource,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => void
  fillRect: (x: number, y: number, width: number, height: number) => void
}

export interface DmdPaint {
  /** Draw the raster into the context, filling it. */
  paint: (raster: DmdRaster) => void
  /** Release the scratch canvas. */
  dispose: () => void
}

/** The unlit glass. Not pure black: a switched-on display with nothing on it still glows a little. */
export const DMD_UNLIT: readonly [number, number, number] = [0.055, 0.035, 0.01]

/**
 * A painter for one strip.
 *
 * Holds a 128 × 32 scratch canvas for the lifetime of the strip rather than allocating one per
 * frame — the allocation is the expensive part, and a per-frame `createElement('canvas')` on a
 * phone is a garbage-collection pause once a second in the middle of a ball.
 */
export function dmdPainter(
  ctx: Canvas2DLike,
  palette: string | undefined,
  makeScratch: (width: number, height: number) => { ctx: Canvas2DLike; source: CanvasImageSource },
): DmdPaint {
  const [r, g, b] = dmdPalette(palette)
  let scratch: { ctx: Canvas2DLike; source: CanvasImageSource } | undefined
  let image: ImageData | undefined

  return {
    paint: (raster) => {
      if (scratch === undefined) scratch = makeScratch(raster.width, raster.height)
      if (image === undefined) image = scratch.ctx.createImageData(raster.width, raster.height)
      const pixels = image.data
      const dots = raster.dots
      for (let i = 0; i < dots.length; i++) {
        // `level / (DMD_LEVELS - 1)`: fifteen has to reach full, not fifteen-sixteenths. The same
        // off-by-one the shader documents, and the same visible symptom — a display that never
        // quite gets bright.
        const level = dots[i]! / (DMD_LEVELS - 1)
        const at = i * 4
        pixels[at] = Math.round(255 * (DMD_UNLIT[0] + (r - DMD_UNLIT[0]) * level))
        pixels[at + 1] = Math.round(255 * (DMD_UNLIT[1] + (g - DMD_UNLIT[1]) * level))
        pixels[at + 2] = Math.round(255 * (DMD_UNLIT[2] + (b - DMD_UNLIT[2]) * level))
        pixels[at + 3] = 255
      }
      scratch.ctx.putImageData(image, 0, 0)
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(scratch.source, 0, 0, ctx.canvas.width, ctx.canvas.height)
    },
    dispose: () => {
      scratch = undefined
      image = undefined
    },
  }
}
